import { API_URLS } from '@helpers/api';
import { clamp } from '@helpers/utils';
import { withQueryParams } from '@hoc/withQueryParams';
import { useApi } from '@hooks/useApi';
import EventListingCard from '@stories/Components/Cards/EventListingCard/EventListingCard';
import PastEventListingCard from '@stories/Components/Cards/PastEventListingCard/PastEventListingCard';
import Filters from '@stories/Components/Listings/Filters/Filters';
import Pagination from '@stories/Components/Listings/Pagination/Pagination';
import LoadingIndicator from '@stories/Components/Misc/LoadingIndicator/LoadingIndicator';
import qs from 'query-string';
import * as React from 'react';
import { ArrayParam, NumberParam, StringParam, useQueryParams, withDefault, } from 'use-query-params';
import S from './EventListing.styles';
import Search from './Search/Search';
const EventListing = (props) => {
    const containerRef = React.useRef(null);
    const [params, setParams] = useQueryParams(Object.assign({
        pageIndex: withDefault(NumberParam, 1),
        pageSize: withDefault(NumberParam, props.initialData?.pastEvents ?? false ? 12 : 10),
        search: withDefault(StringParam, ''),
        sortBy: withDefault(StringParam, props.sortOptions ? props.sortOptions.value : ''),
    }, ...props.filterOptions.map((f) => ({
        [f.name]: withDefault(ArrayParam, []),
    }))));
    const result = useApi({
        initialData: props.initialData,
        url: `${API_URLS.GET_EVENTS}?${qs.stringify(params)}&pastEvents=${props.initialData.pastEvents}&location=${props.initialData?.location ?? ''}`,
    });
    const filterOptions = props.filterOptions.map((f) => ({
        ...f,
        value: params[f.name],
        onChange: (value) => {
            const values = params[f.name];
            if (value === '') {
                setParams({ [f.name]: [value] }, 'pushIn');
            }
            else if (values.includes(value)) {
                setParams({ [f.name]: values.filter((v) => v !== value) }, 'pushIn');
            }
            else {
                setParams({ [f.name]: [...values, value] }, 'pushIn');
            }
            setParams({ pageIndex: 1 }, 'pushIn');
            scrollToTop();
        },
        onClear: () => {
            setParams({ [f.name]: [], pageIndex: 1 }, 'pushIn');
            scrollToTop();
        },
    }));
    const onResetClick = () => {
        setParams({ pageIndex: 1 }, 'replace');
        scrollToTop();
    };
    const sortOptions = props.sortOptions
        ? {
            ...props.sortOptions,
            value: params.sortBy,
            onChange: (value) => {
                setParams({ sortBy: value, pageIndex: 1 }, 'pushIn');
                scrollToTop();
            },
        }
        : undefined;
    const pagingString = `<span>${params.pageIndex > 1 ? `${(params.pageIndex - 1) * params.pageSize + 1} - ` : ''}${clamp(params.pageIndex * params.pageSize, result?.data?.totalCount ?? props.initialData.totalCount)} of ${result?.data?.totalCount ?? props.initialData.totalCount}</span> events`;
    const [hash, setHash] = React.useState('');
    // Check if there is a hash in the URL and set the hash state
    React.useEffect(() => {
        const hashValue = window?.location?.hash?.replace('#', '');
        if (hashValue) {
            setHash(hashValue);
        }
    }, []);
    const hashResult = useApi({
        url: `${API_URLS.GET_EVENTS_PAGE_FROM_HASH}?hash=${hash}&pageSize=${params.pageSize}`,
        validate: () => !!hash,
    });
    // If there is a hash in the URL and the hashResult is available, set the page index to the hash result
    React.useEffect(() => {
        if (hash && hashResult.data && hashResult.data !== params.pageIndex) {
            setParams({ pageIndex: hashResult.data }, 'pushIn');
        }
    }, [hashResult]);
    // Scroll to the correct hash when the page index changes
    React.useEffect(() => {
        if (hash && hashResult.data) {
            const element = document.getElementById(hash);
            if (element) {
                element.scrollIntoView({ behavior: 'smooth' });
            }
            setHash('');
        }
    }, [result.data]);
    return (React.createElement(S.Container, { id: props.anchorId, spacing: "none", ref: containerRef },
        React.createElement(S.TopWrapper, null,
            React.createElement(S.Title, null, props.title),
            React.createElement(Search, { placeholder: props.searchPlaceholder ?? 'What are you looking for?', initialValue: params.search, onSubmit: (value) => {
                    setParams({ search: value, pageIndex: 1 }, 'pushIn');
                    scrollToTop();
                } })),
        React.createElement(Filters, { filterOptions: filterOptions, sortOptions: sortOptions, clearAll: () => onResetClick(), selectedTitle: props.translations?.['global.selectedFilters'] ?? 'Selected Filters', variant: "grey", hasAdvancedFilters: true, viewButtonLabel: props.translations?.['listings.viewEvents'] ?? 'View Events' }),
        result.data && (React.createElement(React.Fragment, null,
            React.createElement(S.PagingSummary, { "data-position": "top", dangerouslySetInnerHTML: { __html: pagingString } }),
            renderResults(),
            result.data?.items?.length > 0 && (React.createElement(React.Fragment, null,
                React.createElement(Pagination, { pageIndex: params.pageIndex, pageSize: params.pageSize, totalCount: result.data.totalCount, onPageChange: (value) => {
                        setParams({ pageIndex: value }, 'pushIn');
                        scrollToTop();
                    } }),
                React.createElement(S.PagingSummary, { "data-position": "bottom", dangerouslySetInnerHTML: { __html: pagingString } }))))),
        result.status === 'loading' && React.createElement(LoadingIndicator, { label: "Loading\u2026", layout: "fixed" })));
    function renderResults() {
        return (React.createElement(S.ResultsContainer, null, !result.data?.items?.length ? (React.createElement(S.NoResultsText, null, "No results found.")) : (React.createElement(React.Fragment, null, result.data.items.map((item) => (React.createElement(React.Fragment, { key: item.id }, props.initialData.pastEvents ? (React.createElement(PastEventListingCard, { ...item })) : (React.createElement(EventListingCard, { ...item, translations: props.translations })))))))));
    }
    function scrollToTop() {
        /** timeout required to ensure scrolling occurs on prev/next buttons */
        setTimeout(() => containerRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'start',
        }), 50);
    }
};
export default withQueryParams(EventListing);
